import { useCallback, useEffect, useRef, useState } from "react";
import { connectSocket, getSocket } from "../../config/socket.config";
import { getChatThread, markChatRead, sendChatMessage } from "../../services/chat.api";

const TYPING_SHOW_MS = 4000;
const TYPING_SEND_EVERY_MS = 2000;

/** Merge messages by id (the socket echoes what we just sent), oldest first. */
const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((m) => [m.id, m]));
  incoming.forEach((m) => byId.set(m.id, m));
  return [...byId.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

/**
 * Everything the chat widget needs for one channel: the thread, live updates
 * over the shared socket, sending, read receipts and "support is typing".
 * `enabled` is false for logged-out visitors — nothing is fetched then.
 */
const useSupportChat = (channel, { enabled, open }) => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(null);
  const [supportTyping, setSupportTyping] = useState(false);
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const thread = await getChatThread(channel, { limit: 30 });
      setConversation(thread.conversation);
      setMessages(thread.messages);
      setHasMore(thread.hasMore);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [channel, enabled]);

  const loadOlder = useCallback(async () => {
    if (!hasMore || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const thread = await getChatThread(channel, { before: messages[0].id, limit: 30 });
      setMessages((prev) => mergeMessages(prev, thread.messages));
      setHasMore(thread.hasMore);
    } finally {
      setLoadingOlder(false);
    }
  }, [channel, hasMore, loadingOlder, messages]);

  // First load (for the unread badge) + live updates.
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    connectSocket();
    const socket = getSocket();

    const onMessage = (payload) => {
      if (payload?.channel !== channel) return;
      setConversation(payload.conversation);
      setMessages((prev) => mergeMessages(prev, [payload.message]));
      if (payload.message?.from === "support") setSupportTyping(false);
    };
    const onConversation = (payload) => {
      if (payload?.channel === channel) setConversation(payload.conversation);
    };
    const onTyping = (payload) => {
      if (payload?.channel !== channel) return;
      setSupportTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setSupportTyping(false), TYPING_SHOW_MS);
    };
    const onRead = (payload) => {
      if (payload?.channel !== channel) return;
      setConversation((prev) => (prev ? { ...prev, supportLastReadAt: payload.supportLastReadAt } : prev));
    };
    // Missed events while disconnected — just reload.
    const onConnect = () => load();

    socket.on("chat:message", onMessage);
    socket.on("chat:conversation", onConversation);
    socket.on("chat:typing", onTyping);
    socket.on("chat:read", onRead);
    socket.on("connect", onConnect);
    return () => {
      socket.off("chat:message", onMessage);
      socket.off("chat:conversation", onConversation);
      socket.off("chat:typing", onTyping);
      socket.off("chat:read", onRead);
      socket.off("connect", onConnect);
      clearTimeout(typingTimer.current);
    };
  }, [channel, enabled, load]);

  // Reading the open widget clears the unread badge (and shows "Seen" to support).
  const unread = conversation?.unread ?? 0;
  useEffect(() => {
    if (!enabled || !open || unread === 0) return;
    markChatRead(channel)
      .then((next) => next && setConversation(next))
      .catch(() => {});
  }, [channel, enabled, open, unread]);

  const send = useCallback(
    async ({ body, attachments }) => {
      const result = await sendChatMessage(channel, { body, attachments });
      setConversation(result.conversation);
      setMessages((prev) => mergeMessages(prev, result.messages));
    },
    [channel],
  );

  const notifyTyping = useCallback(() => {
    if (Date.now() - lastTypingSent.current < TYPING_SEND_EVERY_MS) return;
    lastTypingSent.current = Date.now();
    getSocket().emit("chat:typing", { channel });
  }, [channel]);

  return {
    conversation,
    messages,
    hasMore,
    loading,
    loadingOlder,
    error,
    supportTyping,
    unread: open ? 0 : unread,
    send,
    loadOlder,
    notifyTyping,
    reload: load,
  };
};

export default useSupportChat;
